/**
 * Every chat with the agent, pushed over the conversation the way iOS pushes a
 * list: back on the left, the count in the title, grouped by recency. The open
 * chat is marked. Each row's More opens Rename and Delete (Figma 05 · History);
 * Rename turns the row into its own field, Delete asks once in the row.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import {
  Check,
  ChevronLeft,
  History,
  Loader2,
  MoreHorizontal,
  PencilLine,
  Search,
  SquarePen,
  Trash2,
  X,
} from "lucide-react";
import type { ChatSession } from "@/types/chat.type";
import {
  SESSION_GROUPS,
  sessionDate,
  sessionFullDateFormatter,
  sessionGroup,
  sessionSurface,
  sessionWhen,
} from "@/app/routes/dashboard/components/chatSessionGroups";
import {
  KitMenu,
  KitMenuItem,
  KitMenuPopover,
  KitMenuTrigger,
  KitPressable,
} from "@/app/routes/dashboard/components/kit/Menu";

/** The backend takes a title of 1 to 80 characters. */
export const SESSION_TITLE_MAX = 80;

function RenameField({
  title,
  onSave,
  onCancel,
}: {
  title: string;
  onSave: (next: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, []);

  const save = () => {
    const next = value.trim();
    if (!next || next === title.trim()) onCancel();
    else onSave(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  };

  return (
    <div className="m7r-rename">
      <div className="m7r-rename-field">
        <input
          ref={inputRef}
          value={value}
          maxLength={SESSION_TITLE_MAX}
          aria-label="Chat name"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button type="button" className="m7r-icon-btn m7r-rename-save" aria-label="Save the name" onClick={save}>
          <Check aria-hidden strokeWidth={1.75} />
        </button>
        <button type="button" className="m7r-icon-btn m7r-rename-cancel" aria-label="Cancel" onClick={onCancel}>
          <X aria-hidden strokeWidth={1.75} />
        </button>
      </div>
      <span className="m7r-rename-hint">Enter saves · Esc cancels · {SESSION_TITLE_MAX} characters</span>
    </div>
  );
}

export default function RailHistory({
  mode,
  sessions,
  currentSessionId,
  deletingSessionId,
  onBack,
  onClose,
  onSelect,
  onNew,
  onDelete,
  onRename,
  disabled,
}: {
  mode: "column" | "sheet";
  sessions: ChatSession[];
  currentSessionId: string | null;
  deletingSessionId: string | null;
  onBack: () => void;
  /** The sheet's ×: closes Creo from the list as well as from the chat. */
  onClose: () => void;
  onSelect: (sessionId: string) => void;
  onNew: () => void;
  onDelete: (sessionId: string) => void;
  onRename: (sessionId: string, title: string) => void;
  disabled: boolean;
}) {
  const [query, setQuery] = useState("");
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const deleteRef = useRef<HTMLButtonElement>(null);
  const moreRefs = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    backRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (confirmingId) deleteRef.current?.focus();
  }, [confirmingId]);

  const refocusMore = (sessionId: string) =>
    requestAnimationFrame(() => moreRefs.current.get(sessionId)?.focus());

  const needle = query.trim().toLocaleLowerCase();
  const visible = needle
    ? sessions.filter((s) =>
        `${s.title} ${sessionSurface(s)}`.toLocaleLowerCase().includes(needle),
      )
    : sessions;
  const now = new Date();
  const groups = SESSION_GROUPS.map((group) => ({
    ...group,
    sessions: visible.filter((s) => sessionGroup(s, now) === group.key),
  })).filter((group) => group.sessions.length > 0);
  const count = sessions.length;
  const sheet = mode === "sheet";

  return (
    <>
      <div className="m7r-head">
        <div className="m7r-head-lead">
          {/* On the phone the sheet keeps its own head: the history glyph takes
              you back, as it opened the list (Figma 05 · History on the phone). */}
          <button
            ref={backRef}
            type="button"
            className="m7r-icon-btn"
            aria-label="Back to the chat"
            title="Back to the chat"
            onClick={onBack}
          >
            {sheet ? (
              <History aria-hidden strokeWidth={1.75} />
            ) : (
              <ChevronLeft aria-hidden strokeWidth={1.75} />
            )}
          </button>
        </div>
        <div className="m7r-head-title">
          <strong>Chats</strong>
          <span>
            {count} {count === 1 ? "conversation" : "conversations"}
          </span>
        </div>
        <div className="m7r-head-trail">
          <button
            type="button"
            className="m7r-icon-btn"
            aria-label="New chat"
            title="New chat"
            disabled={disabled}
            onClick={onNew}
          >
            <SquarePen aria-hidden strokeWidth={1.75} />
          </button>
          {sheet ? (
            <button type="button" className="m7r-icon-btn" aria-label="Close Creo" title="Close" onClick={onClose}>
              <X aria-hidden strokeWidth={1.75} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="m7r-history-scroll">
        {count > 0 ? (
          <div className="m7r-search">
            <label>
              <Search aria-hidden strokeWidth={1.75} />
              <input
                value={query}
                placeholder="Find a conversation"
                aria-label="Find a conversation"
                onChange={(e) => {
                  setQuery(e.target.value);
                  setConfirmingId(null);
                }}
              />
              {query ? (
                <button
                  type="button"
                  className="m7r-icon-btn"
                  style={{ inlineSize: 24, blockSize: 24 }}
                  aria-label="Clear the search"
                  onClick={() => setQuery("")}
                >
                  <X aria-hidden strokeWidth={1.75} />
                </button>
              ) : null}
            </label>
          </div>
        ) : null}

        {count === 0 ? (
          <div className="m7r-history-empty">
            <strong>No chats yet</strong>
            <span>Start one with New chat.</span>
          </div>
        ) : visible.length === 0 ? (
          <div className="m7r-history-empty">
            <strong>Nothing matches</strong>
            <span>Try fewer letters.</span>
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.key} aria-label={group.label}>
              <h3 className="m7r-history-group">{group.label}</h3>
              <ul className="m7r-history-list">
                {group.sessions.map((session) => {
                  const current = session.id === currentSessionId;
                  const deleting = deletingSessionId === session.id;
                  const confirming = confirmingId === session.id;
                  const title = session.title.trim() || "Untitled chat";
                  const where = sessionSurface(session);
                  const when = sessionWhen(session, group.key, now);
                  const date = sessionDate(session);

                  if (renamingId === session.id) {
                    return (
                      <li key={session.id} className="m7r-history-row" data-renaming>
                        <RenameField
                          title={title}
                          onSave={(next) => {
                            setRenamingId(null);
                            onRename(session.id, next);
                            refocusMore(session.id);
                          }}
                          onCancel={() => {
                            setRenamingId(null);
                            refocusMore(session.id);
                          }}
                        />
                      </li>
                    );
                  }

                  return (
                    <li
                      key={session.id}
                      className="m7r-history-row"
                      data-current={current || undefined}
                      data-deleting={deleting || undefined}
                    >
                      <button
                        type="button"
                        className="m7r-history-pick"
                        aria-current={current ? "true" : undefined}
                        aria-label={[title, where, when, current ? "open now" : null]
                          .filter(Boolean)
                          .join(", ")}
                        disabled={disabled}
                        onClick={() => onSelect(session.id)}
                      >
                        <span className="m7r-history-title">{title}</span>
                        <span className="m7r-history-meta">
                          {where} ·{" "}
                          <time
                            dateTime={date?.toISOString()}
                            title={date ? sessionFullDateFormatter.format(date) : undefined}
                          >
                            {when}
                          </time>
                        </span>
                      </button>
                      {confirming || deleting ? (
                        <span className="m7r-history-actions">
                          <button
                            ref={deleteRef}
                            type="button"
                            className="m7r-history-delete"
                            disabled={deletingSessionId !== null}
                            onClick={() => onDelete(session.id)}
                          >
                            {deleting ? (
                              <>
                                <Loader2 className="size-3 animate-spin" aria-hidden />
                                Deleting
                              </>
                            ) : (
                              "Delete"
                            )}
                          </button>
                          {deleting ? null : (
                            <button
                              type="button"
                              className="m7r-text-btn"
                              onClick={() => {
                                setConfirmingId(null);
                                refocusMore(session.id);
                              }}
                            >
                              Cancel
                            </button>
                          )}
                        </span>
                      ) : (
                        <>
                          {current ? <span className="m7r-history-open">Open</span> : null}
                          <KitMenuTrigger>
                            <KitPressable>
                              <button
                                ref={(node) => {
                                  if (node) moreRefs.current.set(session.id, node);
                                  else moreRefs.current.delete(session.id);
                                }}
                                type="button"
                                className="m7r-icon-btn m7r-history-more"
                                aria-label={`More for ${title}`}
                                disabled={deletingSessionId !== null}
                              >
                                <MoreHorizontal aria-hidden strokeWidth={1.75} />
                              </button>
                            </KitPressable>
                            <KitMenuPopover placement="bottom end" offset={4} className="!min-w-[180px]">
                              <KitMenu
                                aria-label={`More for ${title}`}
                                onAction={(key) => {
                                  if (key === "rename") {
                                    setConfirmingId(null);
                                    setRenamingId(session.id);
                                  } else if (key === "delete") {
                                    setConfirmingId(session.id);
                                  }
                                }}
                              >
                                <KitMenuItem id="rename" textValue="Rename" className="!text-co-text leading-[18px]">
                                  <span className="m7r-history-menu-row">
                                    Rename
                                    <PencilLine aria-hidden className="size-3 text-co-text2" />
                                  </span>
                                </KitMenuItem>
                                <KitMenuItem id="delete" textValue="Delete" danger className="leading-[18px]">
                                  <span className="m7r-history-menu-row">
                                    Delete
                                    <Trash2 aria-hidden className="size-3" />
                                  </span>
                                </KitMenuItem>
                              </KitMenu>
                            </KitMenuPopover>
                          </KitMenuTrigger>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>
    </>
  );
}
